'use client';

import { useConstant } from '@cc-shopping/hooks/useConstant';
import { useAnalyticsPageView } from '@cc-shopping/platform-analytics/AnalyticsPageView/Context';
import { datadogRum, type RumInternalContext } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
type AcxiomDataCollectionHookParams = {
  slug: string | undefined;
};

// Fires a single Acxiom data-collection request per page view. The endpoint is
// fire-and-forget on the server, so the response carries no data we need.
export const useAcxiomDataCollection = ({
  slug
}: AcxiomDataCollectionHookParams): void => {
  const {
    pageViewId
  } = useAnalyticsPageView();
  const requestId = useConstant(() => crypto.randomUUID());
  useQuery<boolean>({
    queryKey: ['ax-collect', slug],
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () => {
      try {
        const response = await fetch('/api/credit-cards/og/ax-collect', {
          method: 'POST',
          cache: 'no-store',
          credentials: 'include',
          body: JSON.stringify({
            slug,
            pageViewId
          }),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'x-request-id': requestId,
            ...(datadogRum && datadogRum.getInternalContext() ? {
              'x-datadog-rum-session-id': (datadogRum.getInternalContext() as RumInternalContext).session_id,
              'x-datadog-rum-view-id': (datadogRum.getInternalContext() as RumInternalContext).view?.id
            } : {})
          }
        });
        const json = (await response.json().catch(() => null)) as {
          ok?: boolean;
        } | null;
        return response.ok && json?.ok === true;
      } catch (err) {
        datadogRum.addError(err, {
          message: 'useAcxiomDataCollection request failed'
        });
        return false;
      }
    }
  });
};